import { IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { useEffect } from "react";
import { EducationalStandard, ServerFCQ, StudentAssignmentStatus } from "../../../data-types/Types";
import { useData } from "../services/DataProvider";

type QuestionStats = {
  id: string;
  prompt: string;
  difficulty: number | null;
  discrimination: number | null;
  guessing: number | null;
  difficultyStd: number | null;
  responses: number;
  correctRate: number;
  maxGrade: number | null;
};

const Analytics: React.FC = () => {
  const { adminData, queryAdminData, questions, standards, synchingAdmin } = useData();

  useEffect(() => {
    if (!adminData.allStatuses && !synchingAdmin) {
      queryAdminData();
    }
  }, [adminData.allStatuses, queryAdminData, synchingAdmin]);

  const responseCounts = new Map<string, { total: number; correct: number }>();
  (adminData.allStatuses || []).forEach((status: StudentAssignmentStatus) => {
    Object.entries(status.correctness || {}).forEach(([questionId, isCorrect]) => {
      const current = responseCounts.get(questionId) || { total: 0, correct: 0 };
      current.total += 1;
      if (isCorrect) {
        current.correct += 1;
      }
      responseCounts.set(questionId, current);
    });
  });

  const parseGradeNumber = (gradeValue?: string) => {
    if (!gradeValue) {
      return null;
    }
    const numeric = Number.parseInt(gradeValue.replace(/[^0-9]/g, ""), 10);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const standardsById = new Map<string, EducationalStandard>();
  (standards || []).forEach((standard) => {
    if (standard.id) {
      standardsById.set(standard.id, standard);
    }
  });

  const questionStats: QuestionStats[] = (questions || []).map((question: ServerFCQ) => {
    const counts = responseCounts.get(question.id) || { total: 0, correct: 0 };
    const correctRate = counts.total > 0 ? counts.correct / counts.total : 0;
    const maxGrade = (question.standardsCodes || [])
      .map((standardId) => standardsById.get(standardId))
      .map((standard) => parseGradeNumber(standard?.grade))
      .filter((value): value is number => value !== null)
      .reduce((max: number | null, value) => (max === null ? value : Math.max(max, value)), null);
    return {
      id: question.id,
      prompt: question.prompt,
      difficulty: question.irt?.b ?? null,
      discrimination: question.irt?.a ?? null,
      guessing: question.irt?.c ?? null,
      difficultyStd: question.irt?.bStd ?? null,
      responses: counts.total,
      correctRate,
      maxGrade,
    };
  });

  // TODO: Add admin-triggered recalibration/fine-tuning workflow for item parameters.
  const ranked = [...questionStats].sort((a, b) => {
    const aDiff = a.difficulty ?? 0;
    const bDiff = b.difficulty ?? 0;
    return bDiff - aDiff;
  });
  const cellStyle: React.CSSProperties = { padding: "6px 8px" };
  const promptCellStyle: React.CSSProperties = { ...cellStyle, minWidth: "240px" };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Item Difficulty Ranking</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>Items are sorted by difficulty (higher is harder).</p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th align="left" title="Question prompt text." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Prompt</th>
                <th align="right" title="Difficulty parameter (b). Higher is harder." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>b</th>
                <th align="right" title="Estimated uncertainty for difficulty (b std)." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>b std</th>
                <th align="right" title="Discrimination parameter (a). Higher distinguishes ability more." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>a</th>
                <th align="right" title="Guessing parameter (c). Lower bound chance of correct." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>c</th>
                <th align="right" title="Total number of recorded responses." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Responses</th>
                <th align="right" title="Percent of responses marked correct." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Correct %</th>
                <th align="left" title="Maximum grade level across linked standards." style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Max Grade</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((item) => (
                <tr key={item.id} style={{ borderTop: "1px solid #ddd" }}>
                  <td style={promptCellStyle}>{item.prompt}</td>
                  <td align="right" style={cellStyle}>{item.difficulty !== null ? item.difficulty.toFixed(2) : "-"}</td>
                  <td align="right" style={cellStyle}>{item.difficultyStd !== null ? item.difficultyStd.toFixed(2) : "-"}</td>
                  <td align="right" style={cellStyle}>{item.discrimination !== null ? item.discrimination.toFixed(3) : "-"}</td>
                  <td align="right" style={cellStyle}>{item.guessing !== null ? item.guessing.toFixed(2) : "-"}</td>
                  <td align="right" style={cellStyle}>{item.responses}</td>
                  <td align="right" style={cellStyle}>{(item.correctRate * 100).toFixed(1)}%</td>
                  <td style={cellStyle}>{item.maxGrade ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default Analytics;